import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'

export default function detectCycle(questions, answers) {
  if (questions.length === 0) {
    return false
  }

  const graph = makeGraph(questions, answers)

  // Use DFS with colors: white (0) = unvisited, gray (1) = visiting, black (2) = visited
  const colors = {}
  const nodes = Object.keys(graph)

  // Initialize all nodes as white (unvisited)
  nodes.forEach(node => { colors[node] = 0 })

  // DFS function that detects cycles
  function hasCycleDFS(node) {
    if (colors[node] === 1) {
      // Gray node = currently visiting = cycle detected!
      return true
    }

    if (colors[node] === 2) {
      // Black node = already fully processed
      return false
    }

    // Mark as gray (currently visiting)
    colors[node] = 1

    // Check all neighbors
    const neighbors = graph[node] || []
    for (const neighbor of neighbors) {
      if (hasCycleDFS(neighbor)) {
        return true
      }
    }

    // Mark as black (fully processed)
    colors[node] = 2
    return false
  }

  // Check each unvisited node
  for (const node of nodes) {
    if (colors[node] === 0) {
      if (hasCycleDFS(node)) {
        return true
      }
    }
  }

  return false
}

/*
 Alternative: Optimized BFS approach if you prefer BFS
 This uses visited tracking instead of path exploration
*/
export function detectCycleBFS(questions, answers) {
  if (questions.length === 0) {
    return false
  }

  const graph = makeGraph(questions, answers)
  const nodes = Object.keys(graph)

  for (const startNode of nodes) {
    const visited = new Set()
    const queue = [{ node: startNode, path: new Set() }]

    while (queue.length > 0) {
      const { node, path } = queue.shift()

      if (path.has(node)) {
        // Found a cycle
        return true
      }

      if (visited.has(node)) {
        continue
      }

      visited.add(node)
      const newPath = new Set(path)
      newPath.add(node)

      const neighbors = graph[node] || []
      for (const neighbor of neighbors) {
        queue.push({ node: neighbor, path: newPath })
      }
    }
  }

  return false
}

/*
 Get to [{
 questionId: [questionId, ...]
 }]
*/
function makeGraph(questions, answers) {
  function getQuestionIds(question, answers) {
    const { digitOrder } = question
    const questionIds = answers.map(answer => {
      const { action, nextQuestionId } = answer
      if (action === 'next_question') {
        return questions.find(q => q.digitOrder === digitOrder + 1)?.id
      } else if (action === 'go_to_question') {
        return nextQuestionId
      }
    })
    return _.compact(questionIds)
  }

  const questionIdsWithoutAnswers = _.difference(
    _.map(questions, 'id'),
    _.map(answers, 'questionId')
  )
  const fakeQuestionAnswers = questionIdsWithoutAnswers.map(id => ({
    id: uuidv4(),
    questionId: id,
    action: 'next_question'
  }))
  const allAnswers = _.union(answers, fakeQuestionAnswers)

  return _.fromPairs(
    questions.map(q => [
      q.id,
      getQuestionIds(q, allAnswers.filter(a => a.questionId === q.id))
    ])
  )
}